import React, {useState, useRef, useEffect} from "react";
import {
  Warning,
  AddressWrapped,
  Button,
  ErrorMsg
} from "@onegovbugs/onegov-react-components";
import styles from "./ApplicantDetails.module.scss";
import {storePersonalDetails, saveImportExportApplication} from "../../../redux/actions";
import {useDispatch, useSelector} from "react-redux";
import {withRouter} from "react-router-dom";
import {
  isCompleted,
  isValidEmail,
  isAustralianPhoneNumberOrNull,
  isDayTimeNumberOrNull,
  validate,
  scrollToError
} from "../../../utils/Validation";
import moment from "moment";
import ScrollToTop from "../../../utils/ScrollToTop";
import {RedirectIfCaptchaNotSelected} from "../../../utils/Redirect";
import {toCamelCase, format2FAAddress} from "../../../utils/FormatHelper";
import {getAddresUrlPath} from "../../../utils/AddressHelper";

const ApplicantDetails = props => {

  ScrollToTop();
  RedirectIfCaptchaNotSelected(props);

  const addressRef = useRef();
  const dispatch = useDispatch();
  const personalDetails = useSelector(state => state.personalDetails);
  const twoFA = useSelector((state) => state.twoFA);

  const twoFAData = twoFA && twoFA.data ? twoFA.data : {firstName: "", lastName: "", day: "", month: "", year: ""};
  const twoFaVerified = twoFA && twoFA.twoFaVerified ? twoFA.twoFaVerified : false;

  const [firstname, setFirstname] = useState(twoFAData && twoFAData.firstName ? twoFAData.firstName : '');
  const [middlename, setMiddlename] = useState(personalDetails && personalDetails.middlename ? personalDetails.middlename : '');
  const [lastname, setLastname] = useState(twoFAData && twoFAData.lastName ? twoFAData.lastName : '');
  const [dayOfBirth, setDayOfBirth] = useState(twoFAData && twoFAData.day ? twoFAData.day : '');
  const [monthOfBirth, setMonthOfBirth] = useState(twoFAData && twoFAData.month ? twoFAData.month : '');
  const [yearOfBirth, setYearOfBirth] = useState(personalDetails && personalDetails.yearOfBirth ? personalDetails.yearOfBirth : '');
  const [isTwoFaPopulated, setTwoFaPopulated] = useState(personalDetails && personalDetails.isTwoFaPopulated ? personalDetails.isTwoFaPopulated : false);

  const [address, setAddress] = useState(personalDetails?.address ?? {});
  const [showAddress, setShowAddress] = useState(false);
  const [addressPlaceHolder, setAddressPlaceHolder] = useState(null);
  const [emailAddress, setEmailAddress] = useState(personalDetails && personalDetails.emailAddress ? personalDetails.emailAddress : '');
  const [daytimeTelephoneNumber, setDayTimeTelephoneNumber] = useState(personalDetails && personalDetails.dayTimeTelephoneNumber ? personalDetails.dayTimeTelephoneNumber : '');
  const [mobileNumber, setMobileNumber] = useState(personalDetails && personalDetails.mobileNumber ? personalDetails.mobileNumber : '');

  const [showErrors, setShowErrors] = useState(false);
  const [isAddressValid, setIsAddressValid] = useState(true);
  const [isAddressValidating, setAddressValidating] = useState(false);
  const [warnPartialMatch, setWarnPartialMatch] = useState(false);

  const [errors, setErrors] = useState({
    daytimeTelephoneNumber: false,
    mobileNumber: false,
    emailAddress: false,
    address: false,
  });

  const validators = [
    {
      fieldName: 'daytimeTelephoneNumber',
      fieldValue: daytimeTelephoneNumber,
      isValid: v => isCompleted(v) && isDayTimeNumberOrNull(v),
    },
    {
      fieldName: 'mobileNumber',
      fieldValue: mobileNumber,
      isValid: isAustralianPhoneNumberOrNull
    },
    {
      fieldName: 'emailAddress',
      fieldValue: emailAddress,
      isValid: isValidEmail
    },
    {
      fieldName: "address",
      fieldValue: {
        addressPostal: address,
      },
      isValid: (v) =>
        isAddressValid ||
        (!v.addressPostal.lookupValue &&
          !v.addressPostal.enterManually &&
          addressPlaceHolder),
    }
  ];

  const storeData = (finished) => {

    const personalDetails = {
      firstname, middlename, lastname,
      dayOfBirth, monthOfBirth, yearOfBirth,
      emailAddress, mobileNumber,
      dayTimeTelephoneNumber: daytimeTelephoneNumber,
      address: getFinalAdddress(address), isTwoFaPopulated: true,
      step: 1, finished
    };
    dispatch(storePersonalDetails(personalDetails));
  };


  useEffect(() => {
    if (isTwoFaPopulated != true) {
      setEmailAddress(twoFAData && twoFAData.emailAddress ? twoFAData.emailAddress : '')
      setMobileNumber(twoFAData && twoFAData.mobileNumber ? twoFAData.mobileNumber : '')
      setDayTimeTelephoneNumber(twoFAData && twoFAData.phoneNumber ? twoFAData.phoneNumber : '')

      if (twoFAData.addressses) {
        const formattedPostalAddress = format2FAAddress(twoFAData.addressses, "Postal");
        setAddressPlaceHolder(formattedPostalAddress);
      }

    }

  }, [twoFA]);

  useEffect(() => {
    if (twoFAData.addressses) {

      let internationalPostalAddress = undefined;
      if (address.addressNotChanged || typeof address.addressNotChanged === "undefined") {

        const twoFAPostalAddress = twoFAData.addressses.find(
          (a) => a.addressType === "Postal"
        );
        const postalAddressCountry = twoFAPostalAddress.country;
        if (typeof postalAddressCountry === "undefined" || postalAddressCountry.toLowerCase() === "australia") {
          const formattedPostalAddress = format2FAAddress(
            twoFAData.addressses,
            "Postal"
          );
          setAddressPlaceHolder(formattedPostalAddress);
        } else {
          internationalPostalAddress = {
            enterManually: true,
            international: true,
            lookupValue: '',
            addressLine: twoFAPostalAddress.addressLine,
            suburb: twoFAPostalAddress.suburb,
            postcode: twoFAPostalAddress.postCode,
            country: toCamelCase(twoFAPostalAddress.country),
            state: twoFAPostalAddress.state
          };
          setAddress(internationalPostalAddress);
        }
      }
    }
    setShowAddress(true)
  }, []);

  useEffect(() => {
    validate(validators, setErrors);
  }, [daytimeTelephoneNumber, emailAddress, address]);

  const getFinalAdddress = (adddressValue) => {
    let finalAdddress;
    if (
      adddressValue.lookupValue ||
      adddressValue.enterManually == true
    ) {
      finalAdddress = {
        addressNotChanged: false,
        ...adddressValue,
      };
    } else {
      finalAdddress = {
        addressNotChanged: true,
        addressType: "Postal",
      };
    }
    return finalAdddress;
  };
  return (
    <div className={styles.mainCont}>

      <div className={"row"}>
        <div className={"col-12"}>
          <div className={styles.shadowBox}>
            {
              twoFaVerified &&
              (<Warning type={"success"}>
                <p>Record successfully matched.</p>
              </Warning>)
            }
            <h3>{twoFAData.firstName + " " + twoFAData.lastName}</h3>
            <p>Date of
              birth: {moment(twoFAData.month + "/" + twoFAData.day + "/" + twoFAData.year).format("DD/MM/YYYY")}</p>
            <div className={"row " + styles.topMargin24}>
              <div className={"col-12"}>
                <Button
                  onClick={() => {

                    dispatch(
                      storePersonalDetails({
                        ...personalDetails,
                        isTwoFaPopulated: false
                      }));

                    props.history.push(`./individual-licence-information`);

                    // dispatch(process2FA((TwoFaUrl) => {
                    //   sessionStorage["nativespeciesTwoFASource"]="licenceInformation";
                    //   window.location = TwoFaUrl;
                    // }))
                  }}>
                  Change Details
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <hr className={styles.hrLine}/>


      <div className={"row"}>
        <div className={"col-12"}>
          <h2 className={styles.headingH2}>Contact Details</h2>
        </div>
      </div>
      <div className={"row"}>

        <div className={"col-12 col-sm-6"}>
          <label htmlFor={"daytimeTelephoneNumber"}>
            Daytime Telephone Number
          </label>
          <input
            autoComplete="off"
            id={"daytimeTelephoneNumber"}
            className={styles.inputBox}
            placeholder="Daytime Telephone Number"
            value={daytimeTelephoneNumber}
            maxLength="20"
            onChange={obj => {
              setDayTimeTelephoneNumber(obj.target.value);
            }}
          />
          {(daytimeTelephoneNumber == null || daytimeTelephoneNumber == "") && errors.daytimeTelephoneNumber && showErrors ? (
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={"Daytime Telephone Number is required."}
            />
          ) : null}
          {daytimeTelephoneNumber !== null && daytimeTelephoneNumber !== "" && errors.daytimeTelephoneNumber && showErrors ? (
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={"Daytime Telephone Number is invalid."}
            />
          ) : null}
        </div>

        <div className="col-12 col-sm-6">
          <label htmlFor={"mobileNumber"}>Mobile Number (Optional)</label>
          <input
            id={"mobileNumber"}
            className={styles.inputBox}
            placeholder="Mobile Number"
            value={mobileNumber}
            maxLength="20"
            onChange={obj => {
              setMobileNumber(obj.target.value);
            }}
          />
          {errors.mobileNumber && showErrors ?

            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={'Mobile Number is invalid.'}
            />
            : null}
        </div>

      </div>
      <div className={"row"}>
        <div className={"col-12 col-sm-6"}>
          <label htmlFor={"emailAddress"}>
            Email address
          </label>
          <input
            id={"emailAddress"}
            className={styles.inputBox}
            placeholder="Email Address"
            value={emailAddress}
            maxLength="100"
            onChange={obj => {
              setEmailAddress(obj.target.value);
            }}
          />
          {errors.emailAddress && showErrors ?
            <ErrorMsg
              className={"ErrorMsg_error"}
              type={"error"}
              description={'Email Adress is required.'}
            /> : null}
        </div>

      </div>

      <hr className={styles.hrLine}/>


      <div className={"row"}>
        <div className={"col-12"}>
          <h2 className={styles.headingH2}>Postal Address</h2>
        </div>
      </div>

      <div className='row'>
        <div className={`col-12 ${styles.addressContainer}`}>
          {showAddress ?
            <AddressWrapped
              international
              autoValidate
              label={null}
              baseUrl={getAddresUrlPath()}
              ref={addressRef}
              onChange={(data) => {
                setAddress(data.address)
              }}
              value={address ? {...address} : {}}
              placeholder={addressPlaceHolder}
              onValidationChange={setIsAddressValid}
              //  value={{"firstAddress":address}}
              onWarningChange={setWarnPartialMatch}
              onLoading={setAddressValidating}
            /> : null}
        </div>
        {errors.address && showErrors ?
          <div className={`col-12 ${styles.errorContainer}`}>
            <ErrorMsg
              type={"error"}
              description={'Please enter the address and select the right one from the dropdown or complete all the address fields.'}
            />
          </div> : null}
      </div>

      <div className={`row ${styles.buttonCont}`}>
        <div className={`col-12`}>
          <Button
            disabled={isAddressValidating}
            onClick={() => {
              if (validate(validators, setErrors)) {
                setShowErrors(false);
                storeData(true);
                dispatch(
                  saveImportExportApplication("", () => {
                    props.history.push(`./dashboard`);
                  })
                );
              } else {
                setShowErrors(true);
                scrollToError();
              }
            }}
          >
            Continue
          </Button>

          <Button
            disabled={isAddressValidating}
            secondary
            onClick={() => {
              storeData();
              dispatch(
                saveImportExportApplication("", () => {
                  props.history.push(`./dashboard`);
                })
              );
            }}
          >
            Save & exit section
          </Button>

        </div>
      </div>

    </div>
  );
};

export default withRouter(props => <ApplicantDetails {...props} />);
